import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getQuoteForCustomer } from "@/lib/quote-access.functions";
import { PdfPreviewDialog } from "@/components/PdfPreviewDialog";
import { Download, Eye } from "lucide-react";

export const Route = createFileRoute("/_authenticated/account/offertes/$id")({
  head: () => ({ meta: [{ title: "Offerte" }, { name: "robots", content: "noindex" }] }),
  component: QuoteDetail,
});

type LineItem = { description: string; qty: number; unit_price_cents: number };

function QuoteDetail() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const nav = useNavigate();
  const fetchQuote = useServerFn(getQuoteForCustomer);

  const { data: result, isLoading, error } = useQuery({
    queryKey: ["quote", id],
    queryFn: async () => {
      try {
        const res = await fetchQuote({ data: { id } });
        if (!res.ok) {
          console.warn("[quote-detail] access denied", { quoteId: id, reason: res.reason, ref: res.ref });
        } else {
          console.info("[quote-detail] access granted", { quoteId: id, ref: res.ref });
        }
        return res;
      } catch (e: any) {
        console.error("[quote-detail] fetch failed", { quoteId: id, message: e?.message });
        throw e;
      }
    },
    retry: false,
  });

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawing, setDrawing] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);

  const copyRef = async (ref: string) => {
    try {
      await navigator.clipboard.writeText(ref);
      toast.success(`Referentie gekopieerd: ${ref}`);
    } catch {
      toast.error("Kopiëren niet gelukt");
    }
  };

  if (isLoading) return <p>Laden…</p>;
  if (error) {
    const msg = (error as Error).message;
    const refMatch = msg.match(/qa-[a-z0-9-]+/i);
    const ref = refMatch?.[0];
    return (
      <div className="max-w-lg space-y-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900">
        <p className="font-semibold">Er ging iets mis bij het openen van deze offerte.</p>
        <p className="text-xs opacity-80">{msg}</p>
        {ref && (
          <p className="text-xs">
            Referentie:{" "}
            <button
              type="button"
              onClick={() => copyRef(ref)}
              className="rounded bg-red-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-red-900 underline decoration-dotted underline-offset-2 hover:bg-red-200"
              title="Klik om te kopiëren — zoek hiermee in de serverlogs op ‘[quote-access]’"
            >
              {ref}
            </button>
          </p>
        )}
      </div>
    );
  }
  if (!result?.ok) {
    const reason = result?.reason ?? "not_found";
    const ref = result?.ref;
    return (
      <div className="max-w-lg space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <p className="font-semibold">
          {reason === "forbidden"
            ? "U heeft geen toegang tot deze offerte."
            : "Deze offerte bestaat niet (meer)."}
        </p>
        <p className="text-xs opacity-80">
          Offerte-id: <span className="font-mono">{id}</span> · reden: {reason}
        </p>
        {ref && (
          <p className="text-xs">
            Referentie:{" "}
            <button
              type="button"
              onClick={() => copyRef(ref)}
              className="rounded bg-amber-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-amber-900 underline decoration-dotted underline-offset-2 hover:bg-amber-200"
              title="Klik om te kopiëren — zoek hiermee in de serverlogs op ‘[quote-access]’"
            >
              {ref}
            </button>{" "}
            <span className="opacity-70">(klik om te kopiëren, plak in de logs)</span>
          </p>
        )}
      </div>
    );
  }

  const quote = result.quote;


  const items = (Array.isArray(quote.line_items) ? quote.line_items : []) as LineItem[];

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const c = canvasRef.current!; const r = c.getBoundingClientRect();
    const ctx = c.getContext("2d")!; ctx.beginPath(); ctx.moveTo(e.clientX - r.left, e.clientY - r.top);
    setDrawing(true);
  };
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing) return;
    const c = canvasRef.current!; const r = c.getBoundingClientRect();
    const ctx = c.getContext("2d")!; ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#111";
    ctx.lineTo(e.clientX - r.left, e.clientY - r.top); ctx.stroke();
  };
  const end = () => setDrawing(false);
  const clear = () => { const c = canvasRef.current!; c.getContext("2d")!.clearRect(0, 0, c.width, c.height); };

  const sign = async () => {
    if (!signerName.trim()) return toast.error("Vul je naam in voor ondertekening");
    const c = canvasRef.current!;
    const blank = document.createElement("canvas"); blank.width = c.width; blank.height = c.height;
    if (c.toDataURL() === blank.toDataURL()) return toast.error("Zet eerst je handtekening");
    setSubmitting(true);
    const { error } = await supabase
      .from("quotes")
      .update({
        status: "signed",
        signature_data: c.toDataURL("image/png"),
        signed_at: new Date().toISOString(),
        signed_name: signerName.trim(),
      })
      .eq("id", id);
    setSubmitting(false);
    if (error) return toast.error(error.message);
    toast.success("Offerte ondertekend");
    qc.invalidateQueries({ queryKey: ["quote", id] });
    qc.invalidateQueries({ queryKey: ["my-quotes"] });
  };

  const decline = async () => {
    if (!confirm("Weet je zeker dat je deze offerte wilt afwijzen?")) return;
    const { error } = await supabase.from("quotes").update({ status: "declined" }).eq("id", id);
    if (error) return toast.error(error.message);
    nav({ to: "/account/offertes" });
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{quote.title}</h1>
          <p className="text-sm text-muted-foreground">Offerte #{quote.quote_number}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setPdfPreviewOpen(true)}>
            <Eye className="mr-1.5 size-4" />
            Voorbeeld
          </Button>
          <Button type="button" size="sm" asChild>
            <a
              href={`/api/public/quotes/pdf?id=${id}`}
              target="_blank"
              rel="noreferrer"
              download={`Offerte-${quote.quote_number}.pdf`}
            >
              <Download className="mr-1.5 size-4" />
              Offerte (PDF)
            </a>
          </Button>
          <Button type="button" size="sm" variant="secondary" asChild>
            <a
              href={`/api/public/quotes/zip?id=${id}`}
              rel="noreferrer"
              download={`Offerte-${quote.quote_number}.zip`}
            >
              <Download className="mr-1.5 size-4" />
              Alles (ZIP)
            </a>
          </Button>

        </div>
      </div>

      <div className="rounded-lg border bg-card p-5">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-muted-foreground"><th className="py-2">Omschrijving</th><th>Aantal</th><th className="text-right">Prijs</th></tr></thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} className="border-b last:border-0">
                <td className="py-2">{it.description}</td>
                <td>{it.qty}</td>
                <td className="text-right">€ {((it.qty * it.unit_price_cents) / 100).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-4 space-y-1 text-right text-sm">
          <div>Subtotaal: € {(quote.subtotal_cents / 100).toFixed(2)}</div>
          <div>BTW: € {(quote.vat_cents / 100).toFixed(2)}</div>
          <div className="text-lg font-bold">Totaal: € {(quote.total_cents / 100).toFixed(2)}</div>
        </div>
        {quote.notes && <p className="mt-4 text-sm text-muted-foreground">{quote.notes}</p>}
      </div>

      {quote.status === "signed" ? (
        <div className="rounded-lg border border-green-200 bg-green-50 p-5">
          <p className="font-semibold text-green-900">Ondertekend door {quote.signed_name} op {new Date(quote.signed_at!).toLocaleString("nl-NL")}</p>
          {quote.signature_data && <img src={quote.signature_data} alt="handtekening" className="mt-3 h-24 border bg-white" />}
        </div>
      ) : quote.status === "declined" ? (
        <p className="text-[#3a7bd5]">Deze offerte is afgewezen.</p>
      ) : (
        <div className="rounded-lg border bg-card p-5 space-y-3">
          <h2 className="font-semibold">Offerte ondertekenen</h2>
          <div className="space-y-1"><Label>Naam ondertekenaar</Label><Input value={signerName} onChange={(e) => setSignerName(e.target.value)} maxLength={120} /></div>
          <div>
            <Label>Handtekening</Label>
            <canvas
              ref={canvasRef}
              width={500}
              height={150}
              className="mt-1 w-full max-w-md touch-none rounded border bg-white"
              onPointerDown={start}
              onPointerMove={move}
              onPointerUp={end}
              onPointerLeave={end}
            />
            <button type="button" onClick={clear} className="mt-1 text-xs text-muted-foreground hover:underline">Wissen</button>
          </div>
          <div className="flex gap-2">
            <Button onClick={sign} disabled={submitting}>Akkoord & ondertekenen</Button>
            <Button variant="outline" onClick={decline}>Afwijzen</Button>
          </div>
        </div>
      )}

      <PdfPreviewDialog
        open={pdfPreviewOpen}
        onOpenChange={setPdfPreviewOpen}
        title={`Offerte ${quote.quote_number}`}
        description="Voorvertoning van de offerte-PDF"
        fileName={`Offerte-${quote.quote_number}.pdf`}
        source={{ kind: "url", url: `/api/public/quotes/pdf?id=${id}` }}
      />
    </div>
  );
}
